import type {
  CreateSpreadSessionInput,
  DrawSpreadCardInput,
  MoveSpreadCardInput,
  JoinSessionInput,
  JoinSpreadSessionResponse,
  GuestSessionCredentials,
  SessionInvitationView,
  DeckSummary,
  SpreadDetail,
  SpreadSessionSummary,
  SpreadSessionView,
  SpreadSummary,
} from '@creativitas/shared';
import { apiFetch } from '@/lib/api';

export const SPREADS_KEY = ['spreads'] as const;
export const SPREAD_SESSIONS_KEY = ['spread-sessions'] as const;
export const spreadKey = (id: string) => ['spreads', id] as const;
export const spreadSessionKey = (id: string) => ['spread-sessions', id] as const;

export const fetchSpreads = (): Promise<{ items: SpreadSummary[] }> =>
  apiFetch<{ items: SpreadSummary[] }>('/spreads');

export const fetchSpread = (id: string): Promise<SpreadDetail> =>
  apiFetch<SpreadDetail>(`/spreads/${id}`);

export const createSpreadSession = (
  spreadId: string,
  input: CreateSpreadSessionInput,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spreads/${spreadId}/sessions`, { method: 'POST', body: input });

export const fetchSpreadSessions = (): Promise<{ items: SpreadSessionSummary[] }> =>
  apiFetch<{ items: SpreadSessionSummary[] }>('/spread-sessions');

export const fetchSpreadSession = (sessionId: string): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}`);

export const drawSpreadCard = (
  sessionId: string,
  input: DrawSpreadCardInput,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/cards`, {
    method: 'POST',
    body: input,
  });

export const moveSpreadCard = (
  sessionId: string,
  cardId: string,
  input: MoveSpreadCardInput,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/cards/${cardId}`, {
    method: 'PATCH',
    body: input,
  });

export const removeSpreadCard = (
  sessionId: string,
  cardId: string,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/cards/${cardId}`, {
    method: 'DELETE',
  });

/** Ведущий отменяет последнюю карту выбранного участника. */
export const undoSpreadCard = (sessionId: string, participantId: string): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/undo-card`, {
    method: 'POST',
    body: { participantId },
  });

/** Ведущий удаляет участника; его карты остаются в истории сессии. */
export const removeSpreadParticipant = (
  sessionId: string,
  participantId: string,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/participants/${participantId}`, {
    method: 'DELETE',
  });

export const clearSpreadTable = (sessionId: string): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/cards`, { method: 'DELETE' });

export const completeSpreadSession = (sessionId: string): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/complete`, { method: 'POST' });

/** SPR-14 — ведущий открывает или закрывает подключение по ссылке. */
export const setSpreadEntry = (sessionId: string, open: boolean): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/entry`, {
    method: 'POST',
    body: { open },
  });

/** Очередь участников: кто сейчас тянет; `null` — свободный порядок (§бэклог 9). */
export const setSpreadTurn = (
  sessionId: string,
  participantId: string | null,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/turn`, {
    method: 'POST',
    body: { participantId },
  });

export const startSpreadSession = (sessionId: string): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/spread-sessions/${sessionId}/start`, { method: 'POST' });

export const fetchSpreadInvitation = (token: string): Promise<SessionInvitationView> =>
  apiFetch<SessionInvitationView>(`/invitations/${token}`);

export const joinSpreadSession = (
  token: string,
  input: JoinSessionInput,
): Promise<JoinSpreadSessionResponse> =>
  apiFetch<JoinSpreadSessionResponse>(`/invitations/${token}/join`, {
    method: 'POST',
    body: input,
  });

const guestHeaders = (credentials: GuestSessionCredentials) => ({
  Authorization: `Bearer ${credentials.guestToken}`,
});

export const fetchGuestSpreadSession = (
  credentials: GuestSessionCredentials,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/guest-spread-sessions/${credentials.sessionId}`, {
    headers: guestHeaders(credentials),
  });

export const fetchGuestFreeformDecks = (
  credentials: GuestSessionCredentials,
): Promise<{ items: DeckSummary[] }> =>
  apiFetch<{ items: DeckSummary[] }>(`/guest-spread-sessions/${credentials.sessionId}/decks`, {
    headers: guestHeaders(credentials),
  });

export const drawGuestSpreadCard = (
  credentials: GuestSessionCredentials,
  input: DrawSpreadCardInput,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(`/guest-spread-sessions/${credentials.sessionId}/cards`, {
    method: 'POST',
    headers: guestHeaders(credentials),
    body: input,
  });

export const moveGuestSpreadCard = (
  credentials: GuestSessionCredentials,
  cardId: string,
  input: MoveSpreadCardInput,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(
    `/guest-spread-sessions/${credentials.sessionId}/cards/${cardId}`,
    { method: 'PATCH', headers: guestHeaders(credentials), body: input },
  );

export const removeGuestSpreadCard = (
  credentials: GuestSessionCredentials,
  cardId: string,
): Promise<SpreadSessionView> =>
  apiFetch<SpreadSessionView>(
    `/guest-spread-sessions/${credentials.sessionId}/cards/${cardId}`,
    { method: 'DELETE', headers: guestHeaders(credentials) },
  );
